import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { InputText, Button, Checkbox } from '@insurancemenu/imenu-components-lib';
import { useRootStore } from 'store/rootStore';
import { observer } from 'mobx-react-lite';
import FormsSpinner from 'components/common/FormSpinner';
import { UserRoleTypes } from 'types/aaaService';
import SubtitleWithLines from 'components/common/SubtitleWithLines';
import { EXTERNAL_IM_LINKS } from 'global/utils/generalLinks';
import { CODE_MESSAGE } from 'global/utils/codeMessages';
import { getUrlParams } from 'global/hooks/getURLParams';
import ErrorMessage from 'components/common/ErrorMessage';
import SupportBlock from 'components/common/SupportBlock';
import { createStyles } from './styles';

const RegistrationCopy = Object.freeze({
  EE_WELCOME_MESSAGE: (
    <>
      Welcome to the InsuranceMenu portal <br /> for Employers and Employees.
    </>
  ),
  AGENT_WELCOME_MESSAGE: (
    <>Let&apos;s get your account set up so you can start generating proposals and enrolling groups</>
  ),
  EE_WELCOME_TEXT: `To get started you will need to create an account. 
  This is a one time step and you will receive a confirmation email with next steps. 
  If you are not sure why you have received this invitation please speak with your employer or agent.
  `,
  TERMS_OF_USE_LABEL: (
    <>
      I understand and agree with the InsuranceMenu
      <a href={EXTERNAL_IM_LINKS.TERMS_OF_USE} rel="noreferrer" target="_blank">
        Terms of Use
      </a>
      .
    </>
  ),
  AGENT_DATA_PROCESSING_POLICY_LABEL: (
    <>
      By checking this box, I agree to the processing of my personal data as defined by the
      <a href={EXTERNAL_IM_LINKS.PRIVACY_POLICY} rel="noreferrer" target="_blank">
        Privacy Policy
      </a>
      for the purposes of selecting and managing the insurance and financial products of my clients.
    </>
  ),
  EE_DATA_PROCESSING_POLICY_LABEL: (
    <>
      By checking this box, I agree to the processing of my personal data as defined by the
      <a href={EXTERNAL_IM_LINKS.PRIVACY_POLICY} rel="noreferrer" target="_blank">
        Privacy Policy
      </a>
      for the purposes of selecting and managing my insurance and financial products.
    </>
  ),
});

const RegistrationForm: React.FC = observer(() => {
  const classes = createStyles();

  // Mobx store
  const {
    registrationStore: {
      registrationForm,
      isRegistrationFormValid,
      loading,
      errorCode,
      confirmRegistration,
      setDefaultEmail,
    },
  } = useRootStore();

  const { email, password, repeatPassword, isPolicyAccepted, isDataProcessingPolicyAccepted } = registrationForm.fields;

  // Common data
  const history = useHistory();

  const inviteCode = getUrlParams('inviteCode');
  const defaultEmail = getUrlParams('email');
  const roleTypeFromURL = getUrlParams('type').toUpperCase();
  const roleType = (roleTypeFromURL === 'EE' ? 'EE' : 'AGENT') as UserRoleTypes;

  // Role Types check
  const isEERoleType = roleType === 'EE';
  const isAgentRoleType = roleType === 'AGENT';

  useEffect(() => {
    if (defaultEmail) {
      setDefaultEmail(defaultEmail);
    }
  }, []);

  const onContinueWithLogin = (): void => {
    history.push('/login');
  };

  const onSave = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    void confirmRegistration(roleType, defaultEmail, inviteCode, onContinueWithLogin);
  };

  return (
    <form onSubmit={onSave} className={classes.form}>
      <SubtitleWithLines
        text={isAgentRoleType ? RegistrationCopy.AGENT_WELCOME_MESSAGE : RegistrationCopy.EE_WELCOME_MESSAGE}
        className={isAgentRoleType ? classes.subtitle : ''}
      />

      {isEERoleType && <p className={classes.eeWelcomeText}>{RegistrationCopy.EE_WELCOME_TEXT}</p>}
      <div className={classes.formElements}>
        <InputText
          // Employees and Employers do not have the ability to change email, whereas Agents and Carriers do.
          label={isEERoleType ? 'Email Address' : 'Confirm Email Address'}
          name="email"
          value={email.value}
          onChange={registrationForm.onFieldChange}
          className={classes.input}
          disabled={isEERoleType}
          validationPassed={!email.unpassedValidationMsg}
          validationMessage={email.unpassedValidationMsg || ''}
        />
        <InputText
          label="Set Password"
          name="password"
          value={registrationForm.fields.password.value}
          onChange={registrationForm.onFieldChange}
          description="At least 8 characters"
          htmlType="password"
          className={classes.input}
          validationPassed={!password.unpassedValidationMsg}
          validationMessage={password.unpassedValidationMsg || ''}
        />
        <InputText
          label="Confirm Password"
          name="repeatPassword"
          value={registrationForm.fields.repeatPassword.value}
          onChange={registrationForm.onFieldChange}
          htmlType="password"
          className={classes.input}
          validationPassed={!repeatPassword.unpassedValidationMsg}
          validationMessage={repeatPassword.unpassedValidationMsg || ''}
        />
        <Checkbox
          name="isPolicyAccepted"
          label={<span className={classes.termsOfUseLabel}>{RegistrationCopy.TERMS_OF_USE_LABEL}</span>}
          checked={isPolicyAccepted.value}
          onChange={registrationForm.onCheckboxChange}
        />
        <Checkbox
          name="isDataProcessingPolicyAccepted"
          label={
            <span className={classes.policyLabel}>
              {isEERoleType
                ? RegistrationCopy.EE_DATA_PROCESSING_POLICY_LABEL
                : RegistrationCopy.AGENT_DATA_PROCESSING_POLICY_LABEL}
            </span>
          }
          checked={isDataProcessingPolicyAccepted.value}
          onChange={registrationForm.onCheckboxChange}
        />
      </div>
      <div className={errorCode ? classes.buttonWrapperWithError : classes.validButtonWrapper}>
        {errorCode && <ErrorMessage error={CODE_MESSAGE[errorCode]} />}
        <Button
          htmlType="submit"
          type="contained"
          color="primary"
          size="large"
          className={classes.button}
          disabled={!isRegistrationFormValid}
        >
          Save
        </Button>
      </div>
      <SubtitleWithLines text="or" narrow />
      <SupportBlock type="WITH_IM_ACCOUNT" />
      <FormsSpinner display={loading} />
    </form>
  );
});

export default RegistrationForm;
